import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import React from "react";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card";
import { Input } from "../../components/ui/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { fetchWithAuth } from "../../hooks/useAuth";
import { formatDate } from "../../utils/format";

interface AdminLoginAttempt {
  id: number;
  ip: string;
  attempts: number;
  blockedUntil?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface PaginationInfo {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export const SesiuniAdminCard = () => {
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-login-attempts", page, search],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page),
        limit: "20",
        ...(search && { search }),
      });
      const res = await fetchWithAuth(
        `/api/admin/auth/login-attempts?${params}`
      );
      if (!res.ok)
        throw new Error("Eroare la încărcarea încercărilor de login");
      return res.json() as Promise<{
        attempts: AdminLoginAttempt[];
        pagination: PaginationInfo;
      }>;
    },
  });

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const now = new Date();

  return (
    <Card className="bg-white shadow-sm">
      <CardHeader>
        <CardTitle>Încercări de autentificare admin</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="relative mb-4">
          <Search className="w-4 h-4 absolute left-3 top-3 text-gray-500" />
          <Input
            className="pl-9"
            placeholder="Caută după IP..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">IP</TableHead>
                <TableHead className="font-semibold">Încercări</TableHead>
                <TableHead className="font-semibold">Blocat până la</TableHead>
                <TableHead className="font-semibold">
                  Ultima actualizare
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-10">
                    <div className="flex items-center justify-center text-gray-500">
                      Se încarcă...
                    </div>
                  </TableCell>
                </TableRow>
              ) : !data || data.attempts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-10">
                    <div className="text-gray-500">
                      Nu există încercări de login
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                data.attempts.map((a) => {
                  const isBlocked = a.blockedUntil
                    ? new Date(a.blockedUntil) > now
                    : false;
                  return (
                    <TableRow key={a.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium">{a.ip}</TableCell>
                      <TableCell>
                        <Badge className="bg-blue-100 text-blue-800 ring-1 ring-blue-200">
                          {a.attempts}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {a.blockedUntil ? (
                          <Badge
                            className={
                              isBlocked
                                ? "bg-red-100 text-red-800 ring-1 ring-red-200"
                                : "bg-gray-100 text-gray-800 ring-1 ring-gray-200"
                            }
                          >
                            {formatDate(a.blockedUntil)}
                          </Badge>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-gray-700">
                        {formatDate(a.updatedAt)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {data?.pagination &&
          data.pagination.total > data.pagination.pageSize && (
            <div className="flex justify-center mt-4">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  className="px-4"
                >
                  Anterior
                </Button>
                <span className="text-sm text-gray-600">
                  Pagina {data.pagination.page} din {data.pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= data.pagination.totalPages}
                  className="px-4"
                >
                  Următor
                </Button>
              </div>
            </div>
          )}
      </CardContent>
    </Card>
  );
};
